import { useMemo } from "react";
import { z } from "zod";
import {
  ApprovalCard,
  type MetadataItem,
  type SerializableApprovalCard,
} from "~/components/tool-ui/approval-card";
import { HitlLoadError, HitlPending } from "./HitlStatus";
import { approvalWiring } from "./approvalWiring";
import type { HitlToolProps } from "./hitlTools";
import { parseToolBody } from "./parseToolBody";
import type { ToolCallEntry } from "./useTurns";

// Args the backend `public_dataset_trigger_run` tool sends: the pipeline to
// trigger, plus any declared-param overrides (e.g. an auto_build_* rebuild
// flag) the user already consented to in chat.
const TriggerArgsSchema = z.object({
  dag_id: z.string().min(1),
  dag_params: z.record(z.string(), z.boolean()).optional(),
});

interface TriggerArgs {
  dagId: string;
  dagParams: Record<string, boolean>;
}

function parseTriggerArgs(args: string): TriggerArgs | null {
  const view = parseToolBody(args);
  if (view.kind !== "json" || Array.isArray(view.value)) return null;
  const parsed = TriggerArgsSchema.safeParse(view.value);
  if (!parsed.success) return null;
  return { dagId: parsed.data.dag_id, dagParams: parsed.data.dag_params ?? {} };
}

const LIST_PIPELINES_TOOL_NAME = "public_dataset_list_pipelines";

const ListedPipelineSchema = z.object({
  dag_id: z.string(),
  dag_display_name: z.string().optional(),
});

// The display name is never on the trigger call's own wire args — the model
// cannot distort it. Instead, correlate against the turn's own
// `public_dataset_list_pipelines` result (how the model learned `dagId` in
// the first place), same ground-truth pattern as `ConfirmIngestCard`'s
// `findProfile`. The last matching listing call wins, in case of a re-list.
function resolveDagDisplayName(
  toolCalls: readonly ToolCallEntry[] | undefined,
  dagId: string
): string | undefined {
  if (!toolCalls) return undefined;
  const listings = toolCalls.filter((tc) => tc.name === LIST_PIPELINES_TOOL_NAME && tc.result);
  for (let i = listings.length - 1; i >= 0; i--) {
    const view = parseToolBody(listings[i].result);
    if (view.kind !== "json" || !Array.isArray(view.value)) continue;
    for (const raw of view.value) {
      const parsed = ListedPipelineSchema.safeParse(raw);
      if (parsed.success && parsed.data.dag_id === dagId) {
        return parsed.data.dag_display_name;
      }
    }
  }
  return undefined;
}

function buildCard(
  id: string,
  { dagId, dagParams }: TriggerArgs,
  dagDisplayName: string | undefined
): Omit<SerializableApprovalCard, "choice"> {
  // No matching listing in the turn (unusual: the model already knew dagId
  // from an earlier turn) — fall back to dag_id rather than showing nothing.
  const label = dagDisplayName ?? dagId;
  const metadata: MetadataItem[] = [{ key: "Pipeline", value: label }];
  const paramEntries = Object.entries(dagParams);

  return {
    id,
    title: `Trigger ${label}?`,
    metadata,
    sections:
      paramEntries.length > 0
        ? [
            {
              heading: "Parameter overrides",
              items: paramEntries.map(([key, value]) => ({
                label: key,
                value: String(value),
              })),
            },
          ]
        : undefined,
    confirmLabel: "Trigger",
  };
}

// Renders the deferred `public_dataset_trigger_run` call as an ApprovalCard —
// the pipeline being triggered plus any declared-param overrides the agent is
// about to pass through. Reuses the shared ApprovalCard and the exact
// deferred-resume wiring as `ask_for_approval`/`confirm_ingest`.
export const PublicDatasetTriggerCard = ({
  entry,
  isRunning,
  onAnswer,
  turnToolCalls,
}: HitlToolProps) => {
  const args = useMemo(() => parseTriggerArgs(entry.args), [entry.args]);
  const dagDisplayName = useMemo(
    () => (args ? resolveDagDisplayName(turnToolCalls, args.dagId) : undefined),
    [turnToolCalls, args]
  );

  if (!args) {
    return isRunning ? (
      <HitlPending>Preparing pipeline trigger…</HitlPending>
    ) : (
      <HitlLoadError>Couldn&apos;t load this trigger request.</HitlLoadError>
    );
  }

  const card = buildCard(entry.id, args, dagDisplayName);

  return <ApprovalCard {...card} {...approvalWiring(entry, onAnswer)} />;
};
